import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, persistAuthToken, clearAuthToken } from "@/lib/api";

const AuthCtx = createContext(null);

/** Strip the access_token before storing user in React state. */
function splitAuth(payload) {
  if (!payload || typeof payload !== "object") return { token: null, user: payload };
  const { access_token, ...user } = payload;
  return { token: access_token, user };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error("Auth refresh failed:", err);
      }
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // If the account has 2FA on, the server returns a challenge instead of a token.
    if (data && data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token, email: data.email };
    }
    const { token, user: u } = splitAuth(data);
    if (token) await persistAuthToken(token);
    setUser(u);
    return { requires_2fa: false, user: u };
  }, []);

  const loginTwoFactor = useCallback(async (temp_token, code) => {
    const { data } = await api.post("/auth/login-2fa", { temp_token, code });
    const { token, user: u } = splitAuth(data);
    if (token) await persistAuthToken(token);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    const { token, user: u } = splitAuth(data);
    if (token) await persistAuthToken(token);
    setUser(u);
    return u;
  }, []);

  const googleLogin = useCallback(async ({ credential, email, name }) => {
    const body = credential ? { credential } : { email, name };
    const { data } = await api.post("/auth/google", body);
    const { token, user: u } = splitAuth(data);
    if (token) await persistAuthToken(token);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed:", err?.response?.status || err?.message);
    }
    await clearAuthToken();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, loginTwoFactor, register, googleLogin, logout, refresh }),
    [user, ready, login, loginTwoFactor, register, googleLogin, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
